import React from "react";
import { Paperclip, X } from "lucide-react";
import PostedByAgentLine from "./PostedByAgentLine";
import {
  formatDivisionLine,
  formatFeeBlock,
  formatFoot,
  formatHeight,
  formatPositionLabel,
  formatRecruitingLevel,
  formatSalary,
  getDocumentUrl,
  getSalaryKind,
  recruitingLevelPillClass,
  shouldRender,
} from "../../utils/tenderFormatters";

/**
 * "Request details" on the View Pitches surface. Used by the club, staff and
 * coach pitch pages and by nothing else, so it is rebuilt with them.
 *
 * Three defects from the inventory (7.7) are fixed at the source rather than
 * covered up:
 *   4/5. The hardcoded blind text "Division Name" is gone. The real line comes
 *        from `formatDivisionLine`, the platform's own tender-division
 *        formatter that TenderCard already uses, and when the request carries
 *        no division the line is simply not drawn.
 *   4.   `<img src={tender.club.logo}>` with no fallback rendered a broken
 *        image whenever the logo was null. There is a square initials
 *        placeholder now, the same shape as the real logo.
 *   -    The label "Yearly salary" contradicted the request form, which asks
 *        for "Salary per month" (TenderFormDetails.js:513) and the club's own
 *        TenderCard, which says "{Net|Gross} Monthly salary". The modal now
 *        says the same thing as the card describing the same number.
 *
 * Every row is null-safe through `tenderFormatters`: a value the request does
 * not carry does not become a dash.
 */
const TenderDetailsModal = ({ isOpen, onClose, tender }) => {
  if (!isOpen || !tender) return null;

  const clubName = tender.club?.name || null;
  const clubLogo = tender.club?.logo || null;
  const divisionLine =
    formatDivisionLine(tender) ||
    tender.club?.league?.name ||
    tender.club?.competition_name ||
    null;
  const fee = formatFeeBlock(tender);
  const salary = formatSalary(tender);
  const salaryKind = getSalaryKind(tender);
  const documentUrl = getDocumentUrl(tender);
  const requestType = tender.type_of_tender
    ? String(tender.type_of_tender).toUpperCase()
    : null;
  // The Youth / Professional / Women level the club picked on the request form.
  // It was always in the payload (`recruiting_for`) and never rendered here —
  // the V2 build looked at `tender_category`, which only separates players from
  // staff and cannot answer this question at all.
  const recruitingLevel = formatRecruitingLevel(tender.recruiting_for);

  const initials = (clubName || "")
    .trim()
    .split(/\s+/)
    .map((word) => word.charAt(0))
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const rows = [
    {
      label: "Games played last season",
      value: shouldRender(tender.matches_played_last_season)
        ? String(tender.matches_played_last_season)
        : null,
    },
    {
      label: "Age limit",
      value: shouldRender(tender.age) ? String(tender.age) : null,
    },
    { label: "Foot", value: formatFoot(tender.foot) },
    // The request's CRITERION, which is why this one keeps the "Min." — on the
    // pitch card the same number is the player's measured height and is
    // labelled "Height".
    { label: "Min. Height", value: formatHeight(tender.minimum_height) },
    {
      label: salaryKind ? `${salaryKind} Monthly salary` : "Monthly salary",
      value: salary,
    },
  ].filter((row) => shouldRender(row.value));

  const typeBadgeClass =
    tender.type_of_tender === "buy"
      ? "bg-[#009669]"
      : tender.type_of_tender === "loan"
      ? "bg-[#FF9500]"
      : "bg-[#2183FE]";

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-[#111827]/50 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="max-h-[85vh] w-full max-w-[620px] overflow-y-auto rounded-[14px] bg-white p-6 shadow-[0_24px_48px_rgba(17,24,39,0.2)]"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Request details"
      >
        <div className="flex items-center justify-between gap-3">
          <h3 className="m-0 text-[18px] font-bold text-[#111827]">
            Request details
          </h3>
          <div className="flex items-center gap-1">
            {documentUrl ? (
              <a
                href={documentUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Open the attached document"
                title="Open the attached document"
                className="flex h-11 w-11 items-center justify-center rounded-[10px] text-[#6B7280] hover:bg-[#F3F4F6]"
              >
                <Paperclip className="h-4 w-4 rotate-[-44.5deg]" />
              </a>
            ) : null}
            <button
              type="button"
              aria-label="Close"
              onClick={onClose}
              className="-mr-2.5 flex h-11 w-11 items-center justify-center rounded-[10px] text-[#6B7280] hover:bg-[#F3F4F6]"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <PostedByAgentLine attribution={tender.agent_attribution} className="mt-2" />

        <div className="mt-3.5 flex flex-wrap items-center gap-3">
          {clubLogo ? (
            <img
              src={clubLogo}
              alt={clubName || ""}
              className="h-10 w-10 flex-shrink-0 rounded-[10px] object-cover"
            />
          ) : (
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-[10px] bg-[#E9EEF6] text-[13px] font-bold text-[#6B7280]">
              {initials}
            </div>
          )}
          <div className="min-w-0">
            {clubName ? (
              <p className="m-0 text-[15px] font-bold text-[#111827]">{clubName}</p>
            ) : null}
            {divisionLine ? (
              <p className="m-0 mt-px text-[13px] text-[#6B7280]">{divisionLine}</p>
            ) : null}
          </div>
          {requestType ? (
            <span
              className={`ml-auto whitespace-nowrap rounded-full px-3 py-1 text-[12px] font-bold text-white ${typeBadgeClass}`}
            >
              {requestType}
            </span>
          ) : null}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <h4 className="m-0 text-[16px] font-bold capitalize text-[#111827]">
            {formatPositionLabel(tender.position)}
          </h4>
          {/* Beside the position, exactly where the agent request card puts the
              same field. Nothing at all when the request never picked a level. */}
          {recruitingLevel ? (
            <span
              className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${recruitingLevelPillClass(
                tender.recruiting_for,
              )}`}
            >
              {recruitingLevel}
            </span>
          ) : null}
          {tender.is_boosted ? (
            <span className="rounded-md bg-gradient-to-r from-[#FF4D4F80] to-[#FAAA0080] px-2 py-1 text-[10px] font-bold text-black">
              BOOSTED
            </span>
          ) : null}
          {tender.is_lite ? (
            <span className="rounded-md bg-yellow-50 px-2 py-1 text-[10px] font-bold text-yellow-600">
              LITE
            </span>
          ) : null}
        </div>

        {/* whitespace-pre-wrap so the author's paragraphs survive, break-words
            so a URL or a hyphenated club name inside the free text wraps
            instead of running off the right edge of the dialog. The row list
            below already carries min-w-0 break-words; free text needs it too. */}
        {shouldRender(tender.description) ? (
          <p className="m-0 mt-3.5 whitespace-pre-wrap break-words text-[14px] leading-relaxed text-[#374151]">
            {tender.description}
          </p>
        ) : null}

        {fee.value ? (
          <div className="mt-3.5 flex items-center justify-between gap-4 rounded-[10px] bg-[#F5F7FB] px-3.5 py-3">
            <span className="text-[14px] font-semibold text-[#374151]">
              {fee.label}
            </span>
            <span className="text-right text-[15px] font-bold text-[#111827]">
              {fee.value}
            </span>
          </div>
        ) : null}

        <div className="px-0.5 pt-1">
          {rows.map((row) => (
            <div
              key={row.label}
              className="flex items-baseline justify-between gap-4 px-3 py-2"
            >
              <span className="flex-shrink-0 text-[14px] font-medium text-[#6B7280]">
                {row.label}
              </span>
              <span className="min-w-0 break-words text-right text-[14px] font-semibold text-[#111827]">
                {row.value}
              </span>
            </div>
          ))}
        </div>

        {shouldRender(tender.key_requirements) ? (
          <div className="mt-2 px-3 pb-1">
            <p className="m-0 text-[14px] font-semibold text-[#374151]">
              Key requirements
            </p>
            <p className="m-0 mt-1 whitespace-pre-wrap break-words text-[14px] leading-relaxed text-[#111827]">
              {tender.key_requirements}
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default TenderDetailsModal;
